<template>
    <div class="scheduler">
        <div class="chkbox">
            <input
                id="scheduled"
                type="checkbox"
                :checked="schedule.scheduled"
                @change="updateSchedule('scheduled', !schedule.scheduled)"
            />
            <label for="scheduled"></label>
            <label for="scheduled">{{ $t('reports.labels.scheduleReport') }}</label>
        </div>
        <div v-if="schedule.scheduled">
            <div class="info">
                <div class="info-row">
                    <span class="pull-left">{{ $t('reports.timePeriod') }}</span>
                    <span class="pull-right">{{ startScheduledDate }} – {{ endScheduledDate }}</span>
                    <span class="clearfix"></span>
                </div>
                <div class="info-row">
                    <span class="pull-left">{{ $t('reports.nextReportTime') }}</span>
                    <span class="pull-right">{{ sendTime }}</span> <span class="clearfix"></span>
                </div>
                <div class="info-row">
                    <span class="pull-left">{{ $t('common.mariCurrentTime') }}</span>
                    <MariTimeClock class-name="pull-right" />
                    <span class="clearfix"></span>
                </div>
            </div>
            <div :class="{ 'period-time-inline': isPeriodTimeInline }">
                <div class="form-element-label">
                    <span class="label-text">{{ $t('reports.labels.scheduleTime') }}</span>
                </div>
                <div class="form-element-period">
                    <SelectBox
                        :placeholder="$t('reporting.commonLabels.period')"
                        :value="schedule.scheduleType || 7"
                        :options="periods"
                        :search="false"
                        @input="updateSchedule('scheduleType', $event)"
                    />
                </div>
                <div class="form-element-time">
                    <div class="col-xs-12 timepicker-window to-fullwidth">
                        <span v-if="$v.validTimeInput.$invalid" class="tooltiptext">{{
                            errorDisplay($v.validTimeInput, { val: getTimeNoSecondsFormat() })
                        }}</span>
                        <input
                            ref="timePicker"
                            v-model="validTimeInput"
                            :maxlength="timeLenght"
                            :class="['time-input', { tooltiperror: $v.validTimeInput.$invalid }]"
                            :placeholder="$t('reports.labels.time')"
                            @input="updateValidTime($event.target.value)"
                        />
                        <span class="label-place-holder">{{ $t('reports.nextReportTime') }}</span>
                        <i class="icon-time" @mousedown.prevent="toggleEmailingTime($event)"></i>
                    </div>
                    <div>
                        <TimePicker
                            v-model="validTime"
                            :class="['to-fullwidth', { 'to-top': timePickerToTop }]"
                            :opened="openedEmailingTime"
                            @close="closeEmailingTime"
                            @away="closeEmailingTimeAway"
                        />
                    </div>
                    <div class="clearfix"></div>
                </div>
            </div>
            <div class="form-element-emails multiselect-wrapper">
                <span v-if="$v.schedule.scheduleEmails.$invalid" class="tooltiptext">
                    {{ errorDisplay($v.schedule.scheduleEmails, { val: MAX_EMAILS }) }}
                </span>
                <Multiselect
                    ref="scheduleEmailsMultiselect"
                    :value="schedule.scheduleEmails"
                    :placeholder="$t('reports.labels.recipients')"
                    :searchable="searchable"
                    :multiple="true"
                    :taggable="true"
                    :tag-placeholder="$t('reporting.commonLabels.pressEnterToAddEmail')"
                    :options="defaultRecipients"
                    :max="10"
                    :clear-on-select="true"
                    :close-on-select="false"
                    :class="[{ tooltiperror: $v.schedule.scheduleEmails.$invalid }]"
                    @input="updateSchedule('scheduleEmails', $event)"
                    @tag="addMail"
                >
                    <span slot="maxElements">{{ $t('reports.labels.maxEmails', { val: MAX_EMAILS }) }}</span>
                </Multiselect>
            </div>
            <div
                :class="['custom-mail-toggle', { expanded: showCustomText }]"
                @click.prevent="showCustomText = !showCustomText"
            >
                <span class="chewron">
                    <i class="icon-chewron-down"></i>
                </span>
                <span class="custom-mail-text">
                    {{
                        showCustomText
                            ? $t('reports.labels.hideCustomMailText')
                            : $t('reports.labels.showCustomMailText')
                    }}
                </span>
            </div>
            <div v-show="showCustomText" class="wrapper custom-mail-wrapper">
                <span v-if="$v.schedule.scheduleDescription.$invalid" class="tooltiptext">{{
                    $t('validators.maximumCommentLength', { val: MAX_COMMENT_LENGTH })
                }}</span>
                <div
                    class="textarea-with-insidelabel has-5-rows"
                    :class="[{ tooltiperror: $v.schedule.scheduleDescription.$invalid }]"
                >
                    <textarea
                        :value="schedule.scheduleDescription"
                        @input="updateSchedule('scheduleDescription', $event.target.value)"
                    ></textarea>
                    <label>{{ $t('reports.labels.mailText') }}</label>
                </div>
            </div>
        </div>
    </div>
</template>
<script type="text/babel">
import SelectBox from './../common/SelectBox.vue';
import Multiselect from 'moxa-vue-multiselect';
import { errorDisplay, maximumCommentLength, email, correctHHMM } from '../../validators';
import { MAX_EMAILS, MAX_COMMENT_LENGTH, SCHEDULER_PERIODS } from '../../constants';
import { getFullDateFormatNoSeconds, getTimeNoSecondsFormat, formatTime } from '../common/helpers';
import format from 'date-fns/format';
import startOfISOWeek from 'date-fns/startOfISOWeek';
import startOfMonth from 'date-fns/startOfMonth';
import addDays from 'date-fns/addDays';
import addWeeks from 'date-fns/addWeeks';
import addMonths from 'date-fns/addMonths';
import subDays from 'date-fns/subDays';
import subWeeks from 'date-fns/subWeeks';
import subMonths from 'date-fns/subMonths';
import isAfter from 'date-fns/isAfter';
import parse from 'date-fns/parse';
import set from 'date-fns/set';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import isMatch from 'date-fns/isMatch';
import invoke from 'lodash-es/invoke';
import TimePicker from './../common/datetimepicker/TimePicker.vue';
import MariTimeClock from './../common/MariTimeClock.vue';
import { mapState } from 'vuex';

export default {
    components: { MariTimeClock, Multiselect, SelectBox, TimePicker },

    constants: {
        MAX_EMAILS,
        MAX_COMMENT_LENGTH,
    },

    props: {
        schedule: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        isPeriodTimeInline: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            openedFromDate: false,
            openedToDate: false,
            openedEmailingTime: false,
            periods: [
                { value: SCHEDULER_PERIODS.DAILY, name: this.$t('reports.labels.daily') },
                { value: SCHEDULER_PERIODS.WEEKLY, name: this.$t('reports.labels.weekly') },
                { value: SCHEDULER_PERIODS.BIWEEKLY, name: this.$t('reports.labels.biweekly') },
                { value: SCHEDULER_PERIODS.MONTHLY, name: this.$t('reports.labels.monthly') },
            ],
            searchable: true,
            showCustomText: false,
            validTimeInput: '',
            validTime: set(new Date(), { hours: 0, minutes: 0, seconds: 0 }),
            timePickerToTop: false,
            timeLenght: formatTime('00:00').length,
        };
    },

    computed: {
        ...mapState({ defaultRecipients: state => state.defaultRecipients }),

        /**
         * Returns nearest next date of configuration scheduled report
         * @returns {Date} in configured by sys admin date format
         */
        nextReportDate() {
            let currentDate = new Date(this.$store.state.mariCurrentTime);
            let nextReportDate = '';
            switch (this.schedule.scheduleType) {
                case 1:
                    nextReportDate = set(currentDate, this.validEmailingTime);
                    if (isAfter(currentDate, nextReportDate)) {
                        nextReportDate = addDays(nextReportDate, 1);
                    }
                    break;
                case 7:
                    nextReportDate = set(startOfISOWeek(currentDate), this.validEmailingTime);
                    if (isAfter(currentDate, nextReportDate)) {
                        nextReportDate = addWeeks(nextReportDate, 1);
                    }
                    break;
                case 14:
                    if (this.schedule.scheduleLastRun) {
                        nextReportDate = set(
                            startOfISOWeek(this.schedule.scheduleLastRun),
                            this.validEmailingTime
                        );
                        if (isAfter(currentDate, nextReportDate)) {
                            nextReportDate = addWeeks(nextReportDate, 2);
                        }
                    } else {
                        nextReportDate = set(startOfISOWeek(currentDate), this.validEmailingTime);
                        if (isAfter(currentDate, nextReportDate)) {
                            nextReportDate = addWeeks(nextReportDate, 1);
                        }
                    }
                    break;
                case 30:
                    nextReportDate = set(startOfMonth(currentDate), this.validEmailingTime);
                    if (isAfter(currentDate, nextReportDate)) {
                        nextReportDate = addMonths(nextReportDate, 1);
                    }
                    break;
                default:
            }
            return nextReportDate;
        },

        validEmailingTime() {
            if (this.schedule.emailingTime !== '') {
                let [h, m] = this.schedule.emailingTime.split(':');
                return {
                    hours: parseInt(h),
                    minutes: parseInt(m),
                    seconds: 0,
                };
            }
            return { hours: 0, minutes: 0, seconds: 0 };
        },

        /**
         * Returns first date of time period configuration for scheduled report
         * if case 1 (daily period): returns current date as first date in time period
         * if case 7 (weekly period): returns first day (monday) of current week
         * if case 14 (biweekly period): returns first day (monday) of week before current
         * if case 30 (monthly period): returns first date of current month
         * @returns {Date} in format 'MM-DD'
         */
        startScheduledDate() {
            let startScheduledDate = '';
            switch (this.schedule.scheduleType) {
                case 1:
                    startScheduledDate = subDays(this.nextReportDate, 1);
                    break;
                case 7:
                    startScheduledDate = subWeeks(this.nextReportDate, 1);
                    break;
                case 14:
                    startScheduledDate = subWeeks(this.nextReportDate, 2);
                    break;
                case 30:
                    startScheduledDate = subMonths(this.nextReportDate, 1);
                    break;
                default:
            }
            return format(startScheduledDate, getFullDateFormatNoSeconds());
        },

        /**
         * Returns last date of time period configuration for scheduled report
         * @returns {String} formated date
         */
        endScheduledDate() {
            return format(this.nextReportDate, getFullDateFormatNoSeconds());
        },

        sendTime() {
            return format(this.nextReportDate, getFullDateFormatNoSeconds());
        },
    },

    watch: {
        validTime(val) {
            if (!this.$v.validTime.$invalid) {
                // 24H format to store emailing time
                this.schedule.emailingTime = format(val, 'HH:mm');
                // Formatted time string for the timePicker field
                this.validTimeInput = format(this.validTime, getTimeNoSecondsFormat());
            }
        },

        $route() {
            if (this.schedule.emailingTime !== '') {
                let [h, m] = this.schedule.emailingTime.split(':');
                this.validTime = set(new Date(), {
                    hours: parseInt(h),
                    minutes: parseInt(m),
                    seconds: 0,
                });
                // Select correct emailing time in the timePicker field
                this.validTimeInput = format(this.validTime, getTimeNoSecondsFormat());
            }
        },

        '$route.path'() {
            // Hide drop down in each Multiselect when going back/forward in history
            invoke(this.$refs, 'scheduleEmailsMultiselect.deactivate');
        },
    },

    mounted() {
        // Select correct emailing time in the timePicker field
        if (this.schedule.emailingTime !== '') {
            let [h, m] = this.schedule.emailingTime.split(':');
            this.validTime = set(new Date(), {
                hours: parseInt(h),
                minutes: parseInt(m),
                seconds: 0,
            });
        }
        this.validTimeInput = format(this.validTime, getTimeNoSecondsFormat());
        this.$watch('$v.$invalid', () => {
            this.$emit('update', {
                field: 'schedule',
                value: { ...this.schedule },
                valid: !this.$v.$invalid,
            });
        });
    },

    validations() {
        return {
            validTimeInput: {
                incorrectTime: val =>
                    val.length > 0
                        ? isMatch(val, getTimeNoSecondsFormat()) && val.length === this.timeLenght
                        : false,
            },
            validTime: {
                incorrectTime: val => val instanceof Date,
            },
            schedule: {
                emailingTime: {
                    incorrectTime: val => correctHHMM(val) || val === '' || !this.schedule.scheduled,
                },
                scheduleEmails: {
                    emptyEmails: val => (!(val === null) && !(val.length === 0)) || !this.schedule.scheduled,
                    email,
                },

                scheduleDescription: {
                    maximumCommentLength,
                },
            },
        };
    },

    methods: {
        errorDisplay,
        getTimeNoSecondsFormat,

        /**
         * Emits input event to update report configuration
         * @param field name of attribute in report configuration
         * @param value
         * @param event event from buttom if nessesary
         */
        updateSchedule(field, value, event = null) {
            if (event) {
                event.preventDefault();
            }
            if (field === 'scheduled' && value && this.schedule.scheduleEmails.length === 0) {
                this.$nextTick(() => {
                    this.schedule.scheduleEmails = [...this.defaultRecipients];
                });
            }
            this.$emit('update', { field, value, valid: !this.$v.$invalid });
        },

        addMail(newMail) {
            if (newMail !== '') {
                const clippedEmail = newMail.trim();
                this.updateSchedule('scheduleEmails', [...this.schedule.scheduleEmails, clippedEmail]);
            }
        },

        toggleEmailingTime(e) {
            let timePickerHeight = 270; // in case of change don't forget to change $timepicker-height from _variable.scss!
            let me = e.target.parentNode;
            let box = me.getBoundingClientRect();
            let body = document.body;
            let html = document.documentElement;
            let documentHeight = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
            );
            this.timePickerToTop = box.bottom + timePickerHeight > documentHeight;
            this.openedEmailingTime = !this.openedEmailingTime;
        },

        updateValidTime(val) {
            this.validTimeInput = val;
            if (this.validTimeInput !== '') {
                let date = parse(val, getTimeNoSecondsFormat(), new Date());
                if (!this.$v.validTimeInput.$invalid) {
                    let fullDate = set(this.validTime, {
                        hours: getHours(date),
                        minutes: getMinutes(date),
                        seconds: 0,
                    });
                    this.validTime = fullDate;
                }
                this.$v.validTime.$touch();
            }
        },

        closeEmailingTime() {
            this.openedEmailingTime = false;
            this.timePickerToTop = false;
            this.$nextTick(() => {
                this.$refs.timePicker.select();
            });
        },

        closeEmailingTimeAway() {
            this.openedEmailingTime = false;
            this.timePickerToTop = false;
        },
    },
};
</script>
